import React from "react"
import { cn } from "../utils"
import library from "./assets/icon-map"
import type { IconName } from "./assets/icon-map"
import { Tooltip } from "../tooltip"

const SIZES: Record<string, string> = {
  xs: "w-4 h-4",
  sm: "w-5 h-5",
  md: "w-6 h-6",
  lg: "w-8 h-8"
}

type ForbiddenProps = {
  "aria-hidden"?: never
  "aria-label"?: never
  "aria-labelledby"?: never
  role?: never
}

export type IconProps = {
  /** Matches name in Figma, using lowercase and hyphens instead of spaces. */
  name: IconName
  /** Icon size */
  size?: "xs" | "sm" | "md" | "lg"
} & Omit<React.SVGProps<SVGSVGElement>, "name" | keyof ForbiddenProps> &
  ForbiddenProps

const lookupIcon = (iconName: string) => library.find(i => i.name === iconName)

const UnknownIcon = ({ name }: { name: string }) => {
  const questionIcon = lookupIcon("question-circle")
  if (!questionIcon) return null
  const QuestionIcon = questionIcon.component
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <span>
            <QuestionIcon className="text-red-600 animate-bounce" />
          </span>
        }
      />
      <Tooltip.Content position="bottom">
        The icon &ldquo;{name}&rdquo; you&apos;re trying to use doesn&apos;t
        exist. See available icons in the component library&apos;s icon tokens.
      </Tooltip.Content>
    </Tooltip>
  )
}

/**
 * Renders an SVG icon from the design system icon library.
 * Icons are referenced by name and support three sizes.
 */
const Icon = ({ name, className, size = "md", ...props }: IconProps) => {
  className = cn(
    "fill-current inline-block overflow-visible pointer-events-none",
    SIZES[size],
    className
  )
  if (name && typeof name == "string") {
    const icon = lookupIcon(name)
    if (!icon) {
      return <UnknownIcon name={name} />
    }

    const IconComponent = icon.component

    return (
      <IconComponent
        className={className}
        name={name}
        style={{ fontSize: "inherit" }}
        {...props}
        aria-hidden="true"
      />
    )
  }

  return <UnknownIcon name={String(name)} />
}

export type { IconName }
export { Icon }
export default Icon
